import { areEqual, arrayToBase64, base64ToArray, base64ToUint8Array, bufferXor, generateRandomSalt, Hmac, importPassword, pbkdf2Digest, shaDigest, stringToArray, } from '#crypto';
import { CLIENT_KEY, SCRAM_HASH_FUNCTION, SCRAM_ITERATION_COUNT, } from './constants';
export class ScramError extends Error {
    constructor(message) {
        super(message);
    }
}
export const verifyClientProof = async (scramRecord, clientFinalMessage, email, serverFullNonce) => {
    if (clientFinalMessage.fullNonce !== serverFullNonce) {
        return false;
    }
    const calculatedStoredKeyView = await recoverStoredKey(scramRecord, email, clientFinalMessage);
    const storedKeyView = base64ToUint8Array(scramRecord.storedKey);
    return areEqual(storedKeyView, calculatedStoredKeyView);
};
export const generateScramRecord = async (password) => {
    const salt = generateRandomSalt();
    const storedKey = await generateStoredKey(password, salt, SCRAM_ITERATION_COUNT);
    return {
        storedKey: storedKey,
        salt: salt,
        iterations: SCRAM_ITERATION_COUNT,
    };
};
export const generateClientKey = async (password, salt, iterations) => {
    const decodedSalt = base64ToArray(salt);
    const saltedPasswordKey = await deriveSaltedPassword(password, decodedSalt, iterations);
    const hmac = await Hmac.create(SCRAM_HASH_FUNCTION, saltedPasswordKey);
    return hmac.sign(stringToArray(CLIENT_KEY));
};
export const generateStoredKey = async (password, salt, iterations) => {
    const clientKey = await generateClientKey(password, salt, iterations);
    return generateStoredKeyFromClientKey(clientKey);
};
export const generateStoredKeyFromClientKey = async (clientKey) => {
    const storedKeyArray = await shaDigest(clientKey, SCRAM_HASH_FUNCTION);
    return arrayToBase64(storedKeyArray);
};
export const generateClientSignature = async (storedKey, email, fullNonce, salt, iterations) => {
    const authMessage = generateAuthMessage(email, fullNonce, salt, iterations);
    const storedKeyArray = base64ToArray(storedKey);
    const hmac = await Hmac.create(SCRAM_HASH_FUNCTION, storedKeyArray);
    return hmac.sign(stringToArray(authMessage));
};
export const generateClientSignatureFromServerFirst = async (storedKey, email, serverFirstMessage) => generateClientSignature(storedKey, email, serverFirstMessage.fullNonce, serverFirstMessage.salt, serverFirstMessage.iterations);
export const generateClientSignatureFromScramRecord = async (email, fullNonce, scramRecord) => generateClientSignature(scramRecord.storedKey, email, fullNonce, scramRecord.salt, scramRecord.iterations);
export const generateClientProof = async (password, email, serverFirstMessage) => {
    const clientKey = await generateClientKey(password, serverFirstMessage.salt, serverFirstMessage.iterations);
    const storedKey = await generateStoredKeyFromClientKey(clientKey);
    const clientSignature = await generateClientSignatureFromServerFirst(storedKey, email, serverFirstMessage);
    const proof = bufferXor(clientKey, clientSignature);
    return arrayToBase64(proof);
};
export const recoverStoredKey = async (scramRecord, email, clientFinalMessage) => {
    const clientSignature = await generateClientSignatureFromScramRecord(email, clientFinalMessage.fullNonce, scramRecord);
    const recoveredClientKey = recoverClientKey(clientFinalMessage.proof, clientSignature);
    const calculatedStoredKey = await shaDigest(recoveredClientKey, SCRAM_HASH_FUNCTION);
    return new Uint8Array(calculatedStoredKey);
};
export const recoverClientKey = (clientProof, clientSignature) => {
    const decodedProof = base64ToArray(clientProof);
    return bufferXor(decodedProof, clientSignature);
};
const generateAuthMessage = (email, fullNonce, salt, iterations) => {
    return `${email.toLowerCase()},${fullNonce},${salt},${iterations},${fullNonce}`;
};
const deriveSaltedPassword = async (password, salt, iterations) => {
    const passwordKey = await importPassword(password);
    return pbkdf2Digest(passwordKey, salt, iterations, SCRAM_HASH_FUNCTION);
};
